import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { CheckMark, VisaPaymentCard, MasterPaymentCard, MadaPaymentCard } from "../icons/Icons";
import { useGetSubscriptionPlansQuery } from "../../services/public/subscriptionPlans";
import {
  useAdminGetSubscriptionQuery,
  useAdminUpdateSubscriptionMutation,
  useAdminCancelSubscriptionMutation,
  useAdminReactivateSubscriptionMutation,
} from "../../services/organization/subscription";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../Modals/ConfirmationModal";
import RetryPaymentModal from "./modals/RetryPaymentModal";
import ManageSubscriptionModal from "./modals/ManageSubscriptionModal";
import { NoSubscriptionIcon } from "../icons/Icons";
import {
  SUBSCRIPTION_STATUS_ACTIVE,
  SUBSCRIPTION_STATUS_CANCELLED,
  SUBSCRIPTION_STATUS_EXPIRED,
  SUBSCRIPTION_STATUS_PAST_DUE,
  SUBSCRIPTION_STATUS_TRIALING
} from "../../constants";
import LoadingIndicator from "../LoadingIndicator";
import { useCurrency } from "../../hooks/useCurrency";
const SubscriptionTab = () => {
  const { t, i18n } = useTranslation('subscription');
  const currentLanguage = i18n.language;
  const { convertAndFormat } = useCurrency();
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [autoRenewLocal, setAutoRenewLocal] = useState(undefined);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const { data: subscriptionResp, isLoading: isSubscriptionLoading, refetch: refetchSubscription } =
    useAdminGetSubscriptionQuery({}, { refetchOnMountOrArgChange: true });
  const [updateSubscription, { isLoading: isUpdatingSubscription }] =
    useAdminUpdateSubscriptionMutation();
  const [cancelSubscription, { isLoading: isCancellingSubscription }] =
    useAdminCancelSubscriptionMutation();
  const [reactivateSubscription, { isLoading: isReactivating }] =
    useAdminReactivateSubscriptionMutation();

  const { data: plansResp, isLoading: isPlansLoading } = useGetSubscriptionPlansQuery({locale: currentLanguage});

  const subscription = subscriptionResp?.data?.subscription || null;
  const plan = subscription?.plan || null;
  const paymentMethod = subscription?.payment_method || null;

  useEffect(() => {
    if (!subscription) return;
    setAutoRenewLocal(subscription.auto_renew);
  }, [subscription]);

  const autoRenew =
    typeof autoRenewLocal === "boolean"
      ? autoRenewLocal
      : Boolean(subscription?.auto_renew);

  const upgradePlans = useMemo(() => {
    const rawPlans = plansResp?.data || [];
    const currentPlanId = plan?.id;
    return rawPlans
      .filter((p) => p?.is_active)
      .filter((p) => (currentPlanId ? p?.id !== currentPlanId : true));
  }, [plansResp?.data, plan?.id]);

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    try {
      return new Intl.DateTimeFormat(currentLanguage, {
        year: "numeric",
        month: "long",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(iso));
    } catch {
      return "-";
    }
  };

  const formatMoney = (amount) => {
    if (amount == null || amount === "") return "-";
    return convertAndFormat(amount);
  };

  const onToggleAutoRenew = async () => {
    const next = !autoRenew;
    if (!next) {
      // Show confirmation modal for turning OFF
      setShowConfirmModal(true);
      document.getElementById("auto-renew-modal-trigger")?.click();
      return;
    }

    // Direct update for turning ON
    setAutoRenewLocal(true);
    try {
      await updateSubscription({ auto_renew: true }).unwrap();
    } catch {
      setAutoRenewLocal(subscription?.auto_renew);
    }
  };

  const handleConfirmDisableAutoRenew = async () => {
    setShowConfirmModal(false);
    setAutoRenewLocal(false);
    document.getElementById("auto-renew-modal-trigger")?.click();
    try {
      await updateSubscription({ auto_renew: false }).unwrap();
    } catch {
      setAutoRenewLocal(subscription?.auto_renew);
    }
  };

  const handleConfirmCancelSubscription = async () => {
    setShowCancelModal(false);
    document.getElementById("cancel-subscription-modal-trigger")?.click();
    try {
      await cancelSubscription().unwrap();
    } catch (err) {
      console.error("Failed to cancel subscription:", err);
    }
  };

  const handleReactivate = async () => {
    const hasRemainingTime = subscription?.status === SUBSCRIPTION_STATUS_CANCELLED && 
                             subscription?.expires_at && 
                             moment(subscription.expires_at).isAfter(moment());
                             
    if (hasRemainingTime) {
      try {
        await reactivateSubscription().unwrap();
        toast.success(t("subscriptionReactivated", "Your subscription has been successfully reactivated"));
      } catch (err) {
        toast.error(t("commonError", "An error occurred. Please try again."));
      }
    } else {
      setShowManageModal(true);
    }
  };

  return (
    <div className="sub-tab-subscription">
      <p className="sub-tab__description">{t('manageSubscriptions')}</p>



      {/* Active Plan */}
      <div className="sub-section">
        <h2 className="sub-section__title">{t('activePlan')}</h2>
        <div className="active-plan-card">
          {isSubscriptionLoading ? (
              <LoadingIndicator />
          ) : !subscription ? (
            <div className="sub-empty-state">
              <div className="sub-empty-state__icon">
                <NoSubscriptionIcon />
              </div>
              <h3 className="sub-empty-state__title">{t('status.no_subscription')}</h3>
              <p className="sub-empty-state__desc">{t('status.no_subscription_desc')}</p>
            </div>
          ) : (
          <div className="active-plan-card__top">
            <div className="active-plan-card__header">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <h3 className="active-plan-card__name">{plan?.title || ""}</h3>
                <span className={`badge-active ${subscription?.status}`}>
                  <span className="badge-dot"></span>
                  {t(`status.${subscription?.status}`)}
                </span>
              </div>
              {
                [SUBSCRIPTION_STATUS_ACTIVE, SUBSCRIPTION_STATUS_TRIALING, SUBSCRIPTION_STATUS_PAST_DUE].includes(subscription?.status) && (
                  <button
                    type="button"
                    className="btn-cancel-sub btn-cancel-sub--desktop"
                    onClick={() => {
                      setShowCancelModal(true);
                      document.getElementById("cancel-subscription-modal-trigger")?.click();
                    }}
                  >
                    {t('cancelSubscription')}
                  </button>
                )
              }
            </div>

            <div className="active-plan-card__meta">
              {(([SUBSCRIPTION_STATUS_ACTIVE, SUBSCRIPTION_STATUS_TRIALING, SUBSCRIPTION_STATUS_PAST_DUE].includes(
                subscription?.status
              ) &&
                autoRenew) ||
                subscription?.status === SUBSCRIPTION_STATUS_CANCELLED) && (
                <>
                  <div className="meta-block">
                    <p className="meta-label">
                      {subscription?.status === SUBSCRIPTION_STATUS_CANCELLED
                        ? t("expirationDate")
                        : t("nextPayment")}
                    </p>
                    <p className="meta-value">
                      {subscription?.status === SUBSCRIPTION_STATUS_CANCELLED
                        ? formatDateTime(subscription?.expires_at)
                        : formatDateTime(subscription?.renewal_at)}
                    </p>
                  </div>
                  <div className="divider"></div>
                </>
              )}
              {subscription?.renewal_pricing?.total_amount != null && subscription?.renewal_pricing?.total_amount != subscription?.total_amount && (
                <>
                  <div className="meta-block">
                    <p className="meta-label">{t('nextPaymentAmount', 'Next Payment Amount')}</p>
                    <p className="meta-value">
                      {`${formatMoney(
                        subscription.renewal_pricing.total_amount,
                        subscription?.currency
                      )}/${t(subscription?.cadence) || t("month")}`}
                    </p>
                  </div>
                  <div className="divider"></div>
                </>
              )}
              <div className="meta-block">
                <p className="meta-label">{t('paymentMethod')}</p>
                <div className="meta-value d-flex align-items-center gap-2">
                  <span className="visa-badge">
                    {paymentMethod?.brand?.toLowerCase() === "mastercard" && (
                      <MasterPaymentCard width={32} height={20} color="#222357" />
                    )}
                    {paymentMethod?.brand?.toLowerCase() === "visa" && (
                      <VisaPaymentCard width={36} height={12} color="#222357" />
                    )}
                    {paymentMethod?.brand?.toLowerCase() === "mada" && (
                      <MadaPaymentCard width={32} height={20} />
                    )}
                    {!["visa", "mastercard", "mada"].includes(paymentMethod?.brand?.toLowerCase()) && (
                      <VisaPaymentCard width={36} height={12} color="#222357" />
                    )}
                  </span>
                  <span>
                    {paymentMethod?.last4
                      ? `**** ${paymentMethod.last4}`
                      : ""}
                  </span>
                </div>
              </div>
              <div className="divider"></div>
              <div className="meta-block">
                <p className="meta-label">{t('total')}</p>
                <p className="meta-value">
                  {subscription?.total_amount != null
                    ? `${formatMoney(
                        subscription.total_amount,
                        subscription?.currency
                      )}/${t(subscription?.cadence) || t("month")}`
                    : ""}
                </p>
              </div>
            </div>

            {subscription?.status === SUBSCRIPTION_STATUS_PAST_DUE && subscription?.last_failed_transaction && (
              <div className="payment-failure-alert">
                <div className="payment-failure-alert__header">
                  <div className="payment-failure-alert__title-wrap">
                    <span className="payment-failure-alert__icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="currentColor"/>
                      </svg>
                    </span>
                    <p className="payment-failure-alert__title">
                      {subscription.last_failed_transaction.failure_reason?.split(" - ")[1] || t('paymentFailed')}
                    </p>
                  </div>
                  <span className="payment-failure-alert__code">
                    {subscription.last_failed_transaction.failure_reason?.split(" - ")[0]}
                  </span>
                </div>
                <div className="payment-failure-alert__body">
                  <p className="payment-failure-alert__message">
                    {t('paymentFailureLongMsg', {
                      reason: subscription.last_failed_transaction.failure_reason?.split(" - ")[1]?.toLowerCase() || t('commonError')
                    })}
                  </p>
                  <button
                    type="button"
                    className="btn-update-payment"
                    onClick={() => setShowRetryModal(true)}
                  >
                    {t('retryPayment.retryBtn', 'Retry Payment')}
                  </button>
                </div>
              </div>
            )}

            {[SUBSCRIPTION_STATUS_EXPIRED, SUBSCRIPTION_STATUS_CANCELLED].includes(subscription?.status) ? (
              <div className="auto-renew-row">
                <p className="auto-renew-title mb-2">
                  {subscription?.status === SUBSCRIPTION_STATUS_CANCELLED
                    ? t("autoRenewDisabled")
                    : t("subscriptionExpiredTitle", "Your subscription has expired")}
                </p>
                {subscription?.status === SUBSCRIPTION_STATUS_CANCELLED && (
                  <p className="auto-renew-desc">
                    {t("subscriptionContinuationMsg", {
                      date: formatDateTime(subscription?.expires_at),
                    })}
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-subscribe"
                  onClick={handleReactivate}
                  disabled={isReactivating}
                >
                  {isReactivating ? t("common:loading", "Loading...") : t("reactivatePlan", "Reactivate Plan")}
                </button>
              </div>
            ) : (
              <div className="auto-renew-row">
                <div className="auto-renew__header">
                  <p className="auto-renew-title">{t("enableAutoRenew")}</p>
                  <button
                    type="button"
                    className={`toggle-switch ${autoRenew ? "on" : ""}`}
                    onClick={onToggleAutoRenew}
                    aria-label="Toggle Auto Renew"
                    disabled={isUpdatingSubscription}
                  >
                    <span className="toggle-knob" />
                  </button>
                </div>
                <p className="auto-renew-desc">{t("autoRenewDesc")}</p>
              </div>
            )}
            
            {
              [SUBSCRIPTION_STATUS_ACTIVE, SUBSCRIPTION_STATUS_TRIALING, SUBSCRIPTION_STATUS_PAST_DUE].includes(subscription?.status) && (
                <button
                  type="button"
                  className="btn-cancel-sub btn-cancel-sub--mobile"
                  onClick={() => {
                    setShowCancelModal(true);
                    document.getElementById("cancel-subscription-modal-trigger")?.click();
                  }}
                >
                  {t('cancelSubscription')}
                </button>
            )}
          </div>
          )}
          {subscription && (
            <div
              className={`meta-block meta-block--features ${
                featuresOpen ? "is-open" : ""
              }`}
            >
            <button
              className="features-accordion-trigger"
              onClick={() => setFeaturesOpen(!featuresOpen)}
              style={{textAlign: currentLanguage === 'ar' ? 'right' : 'left'}}
            >
              <strong>{t('availableFeatures')}</strong>
              <svg
                width="14"
                height="8"
                viewBox="0 0 14 8"
                fill="none"
                style={{
                  transform: featuresOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                <path
                  d="M1 1L7 7L13 1"
                  stroke="#222"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div className="features-accordion-content">
              <div
                className="upgrade-plan-desc"
                dangerouslySetInnerHTML={{ __html: plan?.description }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>

      {/* Upgrade Plans */}
      <div className="sub-section">
        <h2 className="sub-section__title">{t('upgradePlan')}</h2>
        <div className="upgrade-plans-grid">
          {isPlansLoading ? (
            <div className="p-4">{t('common:loading')}...</div>
          ) : (
            upgradePlans.map((p) => (
              <div key={p.id} className="upgrade-plan-card">
                <div className="upgrade-plan-card__header">
                  <span className="upgrade-plan-name">{p.title}</span>
                  <div className="upgrade-plan-pricing" style={{ flexDirection: 'column', alignItems: currentLanguage === 'ar' ? 'flex-start' : 'flex-end', gap: '2px' }}>
                    <div style={{ fontWeight: p.plan_type === 'enterprise' ? 600 : 'normal' }}>
                      {p.plan_type === 'enterprise' && <>{t('startingAt')}{" "}</>}
                      <span className="price-new">
                        {p.plan_type === 'standard' && p.pricing_model === 'per_seat'
                          ? formatMoney(p.price_per_seat)
                          : formatMoney(p.base_price)}
                      </span>{" "}
                      {t('per')} {t(p?.cadence) || p?.cadence}{p.plan_type === 'enterprise' ? '.' : ''}
                    </div>
                    {p.plan_type === 'enterprise' && p.price_per_seat > 0 && (
                      <div style={{ fontSize: '13px', color: '#6c757d', fontWeight: 'normal' }}>
                        + {formatMoney(p.price_per_seat)} {t('per')} {t(p?.cadence) || p?.cadence} ({t('perSeatPricing')})
                      </div>
                    )}
                    {p.plan_type === 'standard' && p.pricing_model === 'per_seat' && (
                      <div style={{ fontSize: '13px', color: '#6c757d', fontWeight: 'normal' }}>
                        ({t('perSeatPricing')})
                      </div>
                    )}
                  </div>
                </div>
                <p className="upgrade-plan-desc">{p?.subtitle}</p>
                <div className="upgrade-plan-desc" dangerouslySetInnerHTML={{__html: p.description}}></div>
                <div className="upgrade-plan-actions">
                  <button type="button" className="btn btn-subscribe" disabled>
                    {t('upgrade')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-learn-more"
                    onClick={() =>
                      window.open(
                        "https://business.tadarab.com/#business-plans",
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    {t('learnMore')}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <button
        id="auto-renew-modal-trigger"
        style={{ display: "none" }}
        data-bs-toggle="modal"
        data-bs-target="#confirmation-modal"
      />

      <button
        id="cancel-subscription-modal-trigger"
        style={{ display: "none" }}
        data-bs-toggle="modal"
        data-bs-target="#cancel-confirmation-modal"
      />

      <ConfirmationModal
        modalId="confirmation-modal"
        isOpened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        handleConfirmation={handleConfirmDisableAutoRenew}
        confirmationMessage={t("confirmDisableAutoRenew")}
      />

      <ConfirmationModal
        modalId="cancel-confirmation-modal"
        isOpened={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        handleConfirmation={handleConfirmCancelSubscription}
        confirmationMessage={t("confirmCancelSubscription")}
      />
      {
        showRetryModal && (
          <RetryPaymentModal
            isOpened={showRetryModal}
            onClose={() => setShowRetryModal(false)}
            subscription={subscription}
            onSuccess={(status) => {
              setShowRetryModal(false);
              if (status === "processing") {
                toast.info(t("retryPayment.processingMessage", "Payment is being processed. This may take a moment."));
              } else {
                refetchSubscription();
              }
            }}
          />
        )
      }

      <ManageSubscriptionModal
        isOpened={showManageModal}
        onClose={() => setShowManageModal(false)}
        subscription={subscription}
        upgradePlans={upgradePlans}
        onSuccess={(status) => {
          setShowManageModal(false);
          if (status === "processing") {
            toast.info(t("retryPayment.processingMessage", "Payment is being processed. This may take a moment."));
          }
          refetchSubscription();
        }}
      />

    </div>
  );
};

export default SubscriptionTab;
